/* Shared theme analytics: automatically measure links on landing pages only. */
(() => {
  'use strict';

  if (window.__santeviaLpClickTracking || window.Shopify?.designMode) return;
  window.__santeviaLpClickTracking = true;

  const measurementId = 'G-P0VMDGKP8W';
  const pending = [];
  let retryTimer;

  function normalizePath(path) {
    return path.toLowerCase().replace(/\/{2,}/g, '/')
      .replace(/^\/(?:en-ca|en-us|fr-ca|en|fr)(?=\/|$)/, '')
      .replace(/\/$/, '') || '/';
  }

  function isLandingPage() {
    if (document.body?.dataset.pageType !== 'page') return false;
    const path = normalizePath(location.pathname);
    return /^landing(?:-|$)/.test(document.body.dataset.pageTemplate || '')
      || path.startsWith('/pages/lp-')
      || path === '/pages/this-filter-belongs'
      || Boolean(document.querySelector('main [data-landing-role]'));
  }

  function analyticsAllowed() {
    try {
      return window.Shopify?.customerPrivacy?.analyticsProcessingAllowed() === true;
    } catch (_) {
      return false;
    }
  }

  function clearPending() {
    pending.length = 0;
    clearTimeout(retryTimer);
    retryTimer = undefined;
  }

  function flush() {
    clearTimeout(retryTimer);
    retryTimer = undefined;
    if (!analyticsAllowed()) {
      clearPending();
      return;
    }

    const now = Date.now();
    while (pending.length && (pending[0].expires < now
      || pending[0].params.lp_path !== normalizePath(location.pathname))) pending.shift();

    if (typeof window.gtag === 'function') {
      while (pending.length) {
        const event = pending.shift();
        try {
          // Reuse the installed tag and its consent/session context. Never config
          // another tag or manufacture client/session IDs here.
          window.gtag('event', 'lp_click', event.params);
        } catch (_) {
          // Analytics must not interrupt navigation. Do not retry a possibly sent event.
        }
      }
    } else if (pending.length) {
      retryTimer = setTimeout(flush, 100);
    }
  }

  function classify(destination) {
    const host = destination.hostname.toLowerCase().replace(/^www\./, '');
    const internal = host === 'sontevia.shop'
      || host === location.hostname.toLowerCase().replace(/^www\./, '');
    const path = normalizePath(destination.pathname);
    if (!internal) return 'other';
    if (path === normalizePath(location.pathname) && destination.hash) return 'anchor';
    if (path.startsWith('/products/')) return 'product_page';
    if (path === '/cart') return 'open_cart';
    if (/^\/cart\/add(?:\.js)?$/.test(path) || /^\/cart\/\d+:\d+/.test(path)) return 'add_to_cart';
    if (/^\/checkouts?(?:\/|$)/.test(path)) return 'checkout';
    return 'other';
  }

  function placement(link) {
    const section = link.closest('[data-landing-role]');
    const wrapper = link.closest('.shopify-section');
    const region = link.closest('header, footer, main');
    const owner = section || wrapper || region || document.body;
    const label = section?.dataset.landingRole || region?.tagName.toLowerCase() || 'page';
    const position = Array.from(owner.querySelectorAll('a[href]')).indexOf(link) + 1;
    // Shopify's section ID disambiguates repeated sections without editor setup.
    // A short hash keeps generated control identities within GA's string limit.
    let hash = 0;
    for (const char of wrapper?.id || label) hash = ((hash * 31) + char.charCodeAt(0)) | 0;
    return {
      section_id: label.slice(0, 100),
      element_id: (link.id || `${label}_${(hash >>> 0).toString(36)}_link_${position}`).slice(0, 100),
    };
  }

  function track(event) {
    if ((event.type === 'click' && event.button !== 0)
      || (event.type === 'auxclick' && event.button !== 1)
      || !isLandingPage() || !analyticsAllowed()) return;

    const target = event.target instanceof Element ? event.target : event.target?.parentElement;
    const link = target?.closest('a[href]');
    if (!link || link.getAttribute('aria-disabled') === 'true') return;

    let destination;
    try {
      destination = new URL(link.href, location.href);
    } catch (_) {
      return;
    }
    // Never collect mailto/tel payloads, arbitrary DOM text, form values or URL queries.
    if (!['http:', 'https:'].includes(destination.protocol)) return;

    const params = {
      send_to: measurementId,
      lp_path: normalizePath(location.pathname),
      click_action: classify(destination),
      ...placement(link),
      destination_path: normalizePath(destination.pathname),
      destination_host: destination.hostname.toLowerCase().replace(/^www\./, ''),
      tracking_version: 'v1',
      event_id: window.crypto.randomUUID(),
    };
    // Only consented clicks can enter this bounded, in-memory readiness queue.
    if (pending.length === 20) pending.shift();
    pending.push({ params, expires: Date.now() + 5000 });
    flush();
  }

  // Capture phase also sees links whose existing handlers stop propagation.
  // No preventDefault: navigation, keyboard activation and new tabs stay native.
  document.addEventListener('click', track, true);
  document.addEventListener('auxclick', track, true);
  document.addEventListener('visitorConsentCollected', () => {
    if (!analyticsAllowed()) clearPending();
  });
  window.addEventListener('pagehide', clearPending);

  if (isLandingPage() && !window.Shopify?.customerPrivacy && window.Shopify?.loadFeatures) {
    window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], () => {});
  }
})();
